import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import { ButtonNew as Button } from "components/buttonNew/index";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import Search from "components/search/Search";
import FormLayout from "components/form/FormLayout";
import Popup from "components/popup/Popup";
import { IProduct } from "redux/reducers/models/product";
import DataTable from "components/dataTable/DataTable";
import {
  CopyProductGroupProducts,
  DeleteProductGroup,
  DeleteProductGroupProduct,
  GetProductGroups,
  GetProductsInGroup,
  PostProductGroupProduct,
  PutProductGroupProduct,
  PutProductGroup,
} from "api/rpc/2024-04/facilityAdmin/product/groups";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import { GetProduct as FacilityGetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import Spin from "components/spin/spin";
import { cloneDeep, isEqual } from "lodash";
import { isEmpty, valueToString } from "helpers/Helpers";
import MovableDataTableRow from "../../../../../components/dataTable/MovableDataTableRow";
import { IProductGroup, IProductInGroup } from "redux/reducers/models/groups";

interface IProductGroupSaveState {
  title: string;
  addedProducts: IProductInGroup[];
}

interface IProductGroupState {
  id: number;
  prevTitle: string;
  deleteModalVisibility: boolean;
}

interface IProductGroupListProductGroup {
  id: number;
  title: string;
}

interface IProductGroupList {
  productGroups: IProductGroupListProductGroup[];
  selectedProductGroupID: number[];
}

interface IProductState {
  products: IProduct[];
  searchQuery: string;
  addProductVisibility: boolean;
  deleteProductVisibility: boolean;
  copyProductsVisibility: boolean;
  subgroups: IProductGroup[];
  groupHasSubGroups: boolean;
  selectedVariants: number[];
  selectedProducts: IProduct[];
}

const ProductGroup: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { productGroupId } = useParams<{ productGroupId: string }>();
  const dispatch = useAppDispatch();

  const [productGroupSaveState, setProductGroupSaveState] = useState<IProductGroupSaveState>({
    title: "",
    addedProducts: undefined,
  });

  const [productGroupSaveStateBeforeChanges, setProductGroupSaveStateBeforeChanges] =
    useState<IProductGroupSaveState>(null);

  const [productGroupListState, setProductGroupListState] = useState<IProductGroupList>({
    productGroups: [],
    selectedProductGroupID: [],
  });

  const [productGroupState, setProductGroupState] = useState<IProductGroupState>({
    id: undefined,
    prevTitle: "",
    deleteModalVisibility: false,
  });

  const [productState, setProductState] = useState<IProductState>({
    products: null,
    searchQuery: "",
    addProductVisibility: false,
    deleteProductVisibility: false,
    copyProductsVisibility: false,
    subgroups: undefined,
    groupHasSubGroups: false,
    selectedVariants: [],
    selectedProducts: [],
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProductGroup(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProducts(productState.searchQuery, source.token);

    return () => source.cancel();
  }, [productState.searchQuery]);

  async function loadProductGroups(token?: CancelToken) {
    const res = await GetProductGroups(null, token ? false : true, token);

    setProductGroupListState(prevState => ({
      ...prevState,
      productGroups: res.data.map(group => ({
        id: group.id,
        title: group.title,
      })),
    }));
  }

  async function handleDeleteProductGroup() {
    const deleteRes = await DeleteProductGroup(
      {
        id: productGroupState.id,
      },
      true,
    );

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.001")));
      return;
    }

    handleToggleDeleteModalVisibility();
    dispatch(showSuccess(t("secure.facility.settings.product_groups.product_group.002")));
    history.push("/admin/settings/groups");
  }

  async function copyProductsToProductGroup() {
    if (unsavedChangesExist()) {
      const result = await saveProductGroup();

      if (result === "failure") {
        return;
      }
    }

    const postRes = await CopyProductGroupProducts(
      {
        group_ids: productGroupListState.selectedProductGroupID,
        product_ids: productState.selectedVariants,
      },
      true,
    );
    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.003")));
      return;
    }
    void handleCopyProductsVisibility();
    dispatch(showSuccess(t("secure.facility.settings.product_groups.product_group.004")));
  }

  type ProductGroupWithSubgroups = IProductGroup & { subgroups?: IProductGroup[] };

  async function loadProductGroup(token?: CancelToken, id?: string) {
    const newProductGroupId = isEmpty(id) ? productGroupId : id;

    const res = await GetProductGroups(null, token ? false : true, token);

    if (res.status !== StatusCode.OK) {
      return;
    }

    let selectedProductGroup: ProductGroupWithSubgroups;

    const groups: ProductGroupWithSubgroups[] = res.data;
    const subgroups: IProductGroup[] = [];

    groups.forEach(group => {
      if (Number(group.id) === Number(newProductGroupId)) {
        selectedProductGroup = group;
      }

      if (group.type === "group") {
        group.subgroups = [];
      }

      if (group.type === "sub_group") {
        subgroups.push(group);
      }
    });

    subgroups.forEach(subgroup => {
      groups.forEach(group => {
        if (subgroup.parent_id === group.id) {
          group.subgroups.push(subgroup);
        }
      });
    });

    const productsInGroupRes = await GetProductsInGroup(
      { group_id: Number(newProductGroupId) },
      token ? false : true,
      token,
    );

    productsInGroupRes.data?.sort((prev, next) => prev.position - next.position);

    setProductState(prevState => ({
      ...prevState,
      subgroups: selectedProductGroup?.subgroups,
      groupHasSubGroups: selectedProductGroup?.subgroups != null && selectedProductGroup.subgroups.length > 0,
    }));

    setProductGroupState(prevState => ({
      ...prevState,
      id: selectedProductGroup.id,
      prevTitle: selectedProductGroup.title,
    }));

    const updatedGroupSaveState: IProductGroupSaveState = {
      title: selectedProductGroup.title,
      addedProducts: productsInGroupRes.data,
    };

    setProductGroupSaveState(cloneDeep(updatedGroupSaveState));
    setProductGroupSaveStateBeforeChanges(cloneDeep(updatedGroupSaveState));
  }

  async function loadProducts(search: string, token: CancelToken) {
    if (productState.products !== undefined) {
      setProductState(prevState => ({ ...prevState, products: undefined }));
    }

    const res = await FacilityGetProduct({ search: search ?? undefined }, false, token);

    if (res.status !== StatusCode.OK) {
      if (token?.reason) {
        return;
      }
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.005")));
    }

    setProductState(prevState => ({ ...prevState, products: res.status !== StatusCode.OK ? [] : res.data }));
  }

  async function deleteProducts() {
    if (productState.selectedVariants.length <= 0) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.006")));
      return;
    }

    if (unsavedChangesExist()) {
      const result = await saveProductGroup();

      if (result === "failure") {
        return;
      }
    }

    const res = await DeleteProductGroupProduct(
      {
        group_id: Number(productGroupId),
        product_ids: productState.selectedVariants,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.007")));
      return;
    }

    setProductState(prevState => ({ ...prevState, selectedVariants: [] }));
    void loadProductGroup();
    void handleDeleteProductsVisibility();
  }

  async function saveProductGroup(): Promise<"success" | "failure"> {
    if (productGroupSaveState.addedProducts?.length > 0) {
      const putProductGroupProductRes = await PutProductGroupProduct(
        {
          group_id: parseInt(productGroupId),
          products: productGroupSaveState.addedProducts.map(product => ({
            product_id: product.id,
            position: product.position,
          })),
        },
        true,
      );

      if (putProductGroupProductRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating product group product positions"))); // TODO: Translation
        return "failure";
      }
    }

    const editRes = await PutProductGroup(
      {
        id: Number(productGroupId),
        title: productGroupSaveState.title,
      },
      true,
    );

    if (editRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.008")));
      return "failure";
    }

    setProductGroupState(prev => ({ ...prev, prevTitle: productGroupSaveState.title }));
    setProductGroupSaveStateBeforeChanges(cloneDeep(productGroupSaveState));

    return "success";
  }

  async function postProductGroupProduct() {
    const productIds: Array<number> = [];
    productState?.selectedProducts?.forEach(product => {
      productIds.push(product?.id);
    });

    if (productIds?.length <= 0) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.009")));
      return;
    }

    if (unsavedChangesExist()) {
      const result = await saveProductGroup();

      if (result === "failure") {
        return;
      }
    }

    const postRes = await PostProductGroupProduct(
      {
        group_id: parseInt(productGroupId),
        product_ids: productIds,
      },
      true,
    );

    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.010")));
      return;
    }

    setProductState(prevState => ({ ...prevState, selectedProducts: [], searchQuery: "" }));
    void loadProductGroup();
    void handleAddProductsVisibility();
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setProductGroupSaveState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleAllSelectedVariants() {
    if (productState?.selectedVariants?.length !== productGroupSaveState.addedProducts?.length) {
      setProductState(prev => ({
        ...prev,
        selectedVariants: productGroupSaveState.addedProducts.map(variant => variant.id),
      }));
    } else {
      setProductState(prev => ({ ...prev, selectedVariants: [] }));
    }
  }

  function handleSelectVariant(variant_id: number) {
    const foundId = productState.selectedVariants.find(val => val === variant_id);

    if (foundId != null) {
      setProductState(prev => ({
        ...prev,
        selectedVariants: prev.selectedVariants.filter(val => val !== variant_id),
      }));
    } else {
      setProductState(prev => ({ ...prev, selectedVariants: [...prev.selectedVariants, variant_id] }));
    }
  }

  function handleAddProductsVisibility() {
    setProductState(prevState => ({ ...prevState, addProductVisibility: !prevState.addProductVisibility }));
  }

  function handleSelectProductGroupId(sheet_id: number) {
    const foundId = productGroupListState.selectedProductGroupID.find(val => val === sheet_id);

    if (foundId != null) {
      setProductGroupListState(prev => ({
        ...prev,
        selectedProductGroupID: prev.selectedProductGroupID.filter(val => val !== sheet_id),
      }));
    } else {
      setProductGroupListState(prev => ({
        ...prev,
        selectedProductGroupID: [...prev.selectedProductGroupID, sheet_id],
      }));
    }
  }

  function handleCopyProductsVisibility() {
    if (!productState.copyProductsVisibility && productGroupListState.productGroups.length <= 0) {
      void loadProductGroups();
    }

    setProductState(prevState => ({ ...prevState, copyProductsVisibility: !prevState.copyProductsVisibility }));
  }

  function handleToggleDeleteModalVisibility() {
    setProductGroupState(prevState => ({ ...prevState, deleteModalVisibility: !prevState.deleteModalVisibility }));
  }

  function handleDeleteProductsVisibility() {
    if (productState.selectedVariants.length <= 0) {
      dispatch(showError(t("secure.facility.settings.product_groups.product_group.011")));
      return;
    }
    setProductState(prevState => ({ ...prevState, deleteProductVisibility: !prevState.deleteProductVisibility }));
  }

  function handleSearch(searchValue: string) {
    setProductState(prevState => ({ ...prevState, searchQuery: searchValue }));
  }

  function handleSelectProduct(productId: number) {
    const currentProduct = cloneDeep(productState.products.find(product => product.id === productId));
    const updatedSelectedProducts = [...productState.selectedProducts];
    const foundProductIndex = updatedSelectedProducts?.findIndex(
      selectedProduct => selectedProduct?.id === currentProduct?.id,
    );

    // Single variant selected
    if (foundProductIndex === -1) {
      // Add product and single variant
      const updatedCurrentProduct = { ...currentProduct };
      updatedSelectedProducts.push(updatedCurrentProduct);
    } else {
      updatedSelectedProducts?.splice(foundProductIndex, 1);
    }

    setProductState(prevState => ({ ...prevState, selectedProducts: updatedSelectedProducts }));
  }

  const primaryAction = {
    content: t("secure.facility.settings.product_groups.product_group.012"),
    action: saveProductGroup,
  };

  const showDeleteAction = {
    content: t("secure.facility.settings.product_groups.product_group.013"),
    action: handleToggleDeleteModalVisibility,
  };

  function navigateToEditProductGroup(id: string) {
    history.push("/admin/settings/groups/" + id);
    void loadProductGroup(null, id);
    // window.location.href = url;
    // window.location.reload();
  }

  function unsavedChangesExist() {
    if (productGroupSaveStateBeforeChanges == null) {
      return false;
    }

    return !isEqual(productGroupSaveStateBeforeChanges, productGroupSaveState);
  }

  return (
    <>
      <Page
        title={isEmpty(productGroupState.prevTitle) ? " " : productGroupState.prevTitle}
        narrow
        primaryAction={primaryAction}
        secondaryActions={[showDeleteAction]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveProductGroup,
          onCancel: () => setProductGroupSaveState(cloneDeep(productGroupSaveStateBeforeChanges)),
        }}
        breadcrumbs={[
          {
            prefix: true,
            label: t("secure.facility.settings.product_groups.product_group.014"),
            url: "/admin/settings/groups",
          },
        ]}
      >
        <Card style={{ marginBottom: "50px" }}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={productGroupSaveState.title}
                  label={t("secure.facility.settings.product_groups.product_group.015")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("secure.facility.settings.product_groups.product_group.016")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
          <Card.Section>
            <div className="product-group-header">
              <div className="title-header">
                {productState.subgroups?.length > 0
                  ? t("secure.facility.settings.product_groups.product_group.017")
                  : t("Products")}
              </div>

              {!productState.groupHasSubGroups && (
                <div className="flex items-center">
                  <Button className="ml-2" type="secondary" size="xsmall" onClick={handleAddProductsVisibility}>
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />{" "}
                    <span>{t("secure.facility.settings.product_groups.product_group.024")}</span>
                  </Button>

                  <Button className="ml-2" type="secondary" size="xsmall" onClick={handleDeleteProductsVisibility}>
                    <FontAwesomeIcon icon={["far", "trash-can"]} className="mr-2" />{" "}
                    <span>{t("secure.facility.settings.product_groups.product_group.037")}</span>
                  </Button>
                </div>
              )}
            </div>

            <DataTable
              enableDragAndDrop={!productState?.groupHasSubGroups}
              columns={
                !productState.groupHasSubGroups
                  ? [
                      {
                        label: "",
                        width: "7%",
                        content: (
                          <Checkbox
                            onChange={() => handleToggleAllSelectedVariants()}
                            size="small"
                            checked={
                              productState?.selectedVariants?.length === productGroupSaveState.addedProducts?.length
                            }
                          />
                        ),
                      },
                      { label: t("secure.facility.settings.product_groups.product_group.018"), width: "31%" },
                      { label: t("secure.facility.settings.product_groups.product_group.019"), width: "31%" },
                      { label: t("secure.facility.settings.product_groups.product_group.020"), width: "31%" },
                    ]
                  : [
                      { label: t("secure.facility.settings.product_groups.product_group.021"), width: "31%" },
                      { label: t("secure.facility.settings.product_groups.product_group.022"), width: "31%" },
                    ]
              }
              loading={productGroupSaveState.addedProducts === undefined && productState.subgroups === undefined}
            >
              {!productState?.groupHasSubGroups ? (
                <Fragment>
                  {productGroupSaveState.addedProducts?.map(product => {
                    return (
                      <MovableDataTableRow
                        key={product.id}
                        id={product.id}
                        position={product.position}
                        className="clickable"
                        onClick={() => handleSelectVariant(product.id)}
                        rows={productGroupSaveState.addedProducts}
                        setRows={setAddedProductsState =>
                          setProductGroupSaveState(prev => ({
                            ...prev,
                            addedProducts: setAddedProductsState(prev.addedProducts),
                          }))
                        }
                      >
                        <td>
                          <Checkbox
                            onChange={e => e.stopPropagation()}
                            size="small"
                            checked={productState?.selectedVariants?.includes(product.id)}
                          />
                        </td>
                        <td>{product?.title}</td>
                        <td>{product?.subtitle}</td>
                        <td>{product?.type}</td>
                      </MovableDataTableRow>
                    );
                  })}
                </Fragment>
              ) : (
                <Fragment>
                  {productState.subgroups?.map((subgroup, index) => {
                    return (
                      <tr
                        key={index}
                        className="clickable"
                        onClick={() => navigateToEditProductGroup(String(subgroup.id))}
                      >
                        <td>{subgroup?.title}</td>
                        <td>{valueToString(subgroup?.type)}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              )}
            </DataTable>
          </Card.Section>
        </Card>
      </Page>

      {/* Add Products Sheet */}
      <div className="pricing-sheets-products-modal">
        <Sheet
          closable
          onCancel={handleAddProductsVisibility}
          size="medium"
          open={productState.addProductVisibility}
          cancelText={t("secure.facility.settings.product_groups.product_group.023")}
          okText={t("secure.facility.settings.product_groups.product_group.024")}
          title={t("secure.facility.settings.product_groups.product_group.025")}
          okDisabled={productState?.selectedProducts?.length === 0}
          onOk={postProductGroupProduct}
        >
          <Search
            searchCallback={searchValue => handleSearch(searchValue)}
            historyKey="product_group_products"
            placeholder={t("secure.facility.settings.product_groups.product_group.026")}
          />

          <div className="product-select-table">
            {productState.products ? (
              <div>
                {productState.products
                  ?.filter(
                    product =>
                      !productGroupSaveState.addedProducts?.some(addedProduct => addedProduct.id === product.id),
                  )
                  ?.map((product: IProduct, index: number) => {
                    const foundProduct = productState.selectedProducts?.find(
                      selectedProduct => selectedProduct?.id === product?.id,
                    );

                    return (
                      <div
                        key={index}
                        className={classNames("table-row product-header", {})}
                        onClick={() => handleSelectProduct(product.id)}
                      >
                        <div>
                          <Checkbox
                            size="medium"
                            labelHidden
                            checked={foundProduct ? true : false}
                            onChange={() => {}}
                          />
                        </div>
                        <div>
                          <p>{product?.preferred_title ? product?.preferred_title : product.title}</p>
                          {product?.subtitle && <p>{product.subtitle}</p>}
                        </div>
                        <div>{product.vendor_title}</div>
                        {/* <div>{singleVariant && product?.variants[0]?.sku}</div> */}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="spinner">
                <Spin />
              </div>
            )}
          </div>
        </Sheet>
      </div>

      {/* Copy Products Sheet */}
      <Sheet
        closable
        title={t("secure.facility.settings.product_groups.product_group.027")}
        size="medium"
        open={productState.copyProductsVisibility}
        cancelText={t("secure.facility.settings.product_groups.product_group.028")}
        onCancel={handleCopyProductsVisibility}
        okText={t("secure.facility.settings.product_groups.product_group.029")}
        onOk={copyProductsToProductGroup}
      >
        <Card>
          <DataTable
            columns={[{ label: "" }, { label: t("secure.facility.settings.product_groups.product_group.030") }]}
            loading={productGroupListState.productGroups === undefined}
          >
            {productGroupListState.productGroups?.map((group, index) => {
              return (
                <tr key={index} className="clickable" onClick={() => handleSelectProductGroupId(group.id)}>
                  <td>
                    <Checkbox
                      onChange={e => e.stopPropagation()}
                      size="medium"
                      checked={productGroupListState?.selectedProductGroupID?.includes(group.id)}
                    />
                  </td>
                  <td>{group.title}</td>
                </tr>
              );
            })}
          </DataTable>
        </Card>
      </Sheet>

      {/* Delete Product Group Warning */}
      <Popup
        open={productGroupState.deleteModalVisibility}
        type="warning"
        title={t("secure.facility.settings.product_groups.product_group.031")}
        description={t("secure.facility.settings.product_groups.product_group.032")}
        onOk={handleDeleteProductGroup}
        okText={t("secure.facility.settings.product_groups.product_group.033")}
        onCancel={handleToggleDeleteModalVisibility}
        backDropCancel={true}
      />

      {/* Delete Product Group Product */}
      <Popup
        open={productState.deleteProductVisibility}
        type="warning"
        title={t("secure.facility.settings.product_groups.product_group.034")}
        description={t("secure.facility.settings.product_groups.product_group.035")}
        onOk={deleteProducts}
        okText={t("secure.facility.settings.product_groups.product_group.036")}
        onCancel={handleDeleteProductsVisibility}
        backDropCancel={true}
      />
    </>
  );
};

export default ProductGroup;
